// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Aircraft-flights panel data layer: the per-aircraft flights list,
// the sort comparator (date / duration / route with a stable
// title+id fallback), the active sort header toggle, the search +
// flag + trend + date-range filter pipeline, the active-filter count
// + control sync, the per-summary flag-class derivation, and the
// per-panel "review/watch/clear" summary chip renderer.
//
// renderAircraftFlightsPanel itself stays in app.js (huge dep
// surface). This module is the data + control state behind it.
//
// Exposed as a factory because the cluster reads/writes the two
// aircraftFlightsSort + aircraftFlightsFilters mutable globals plus
// many late-bound app.js helpers.

import { flightRouteSummaryText } from "./flight-route-display.js";
import { flightCodeFromText, flightSourcePaths } from "./flight-display-model.js";

function sourceFilename(value) {
  const text = String(value ?? "").trim();
  return text ? text.split(/[\\/]/).pop() : "";
}

/** Exact source names retained for hover/focus provenance in the Flights table. */
export function aircraftFlightSourceFilenames(entry) {
  const names = [
    entry?.original_filename,
    ...flightSourcePaths(entry),
  ].map(sourceFilename).filter(Boolean);
  return [...new Set(names)];
}

/** The flight/log reference embedded by the source monitor, when available. */
export function aircraftFlightMonitorReference(entry) {
  const jpiFlightNumber = entry?.jpiFlight?.flightNumber
    ?? entry?.segment_metadata?.jpiFlight?.flightNumber;
  if (jpiFlightNumber !== null && jpiFlightNumber !== undefined && String(jpiFlightNumber).trim()) {
    return `Flight ${String(jpiFlightNumber).trim()}`;
  }
  const values = [
    entry?.title,
    entry?.original_filename,
    ...flightSourcePaths(entry),
    entry?.id,
  ];
  for (const value of values) {
    const flightMatch = String(value ?? "").match(/\bFlight\s*#?\s*\d+\b/i)?.[0];
    if (flightMatch) return flightMatch.replace(/\s+/g, " ");
    const code = flightCodeFromText(value);
    if (code) return code;
  }
  return "";
}

export function createAircraftFlightsPanelData({
  escapeHtml,
  trendDateValue,
  flightDisplayTitle,
  flightDateLabel,
  selectedAircraftWorkspace,
  visibleFlightEntriesByAircraft,
  aircraftTrendRowsForActivity,
  aircraftFlightDurationSecondsFromTrendRows,
  renderAircraftFlightsPanel,
  flightIncludedInTrendAnalysis,
  flightAutoExcludedFromTrendAnalysis,
  flightReportAvailable,
  flightFlagSummary,
  aircraftFlightDateRangeLabel,
  getAircraftFlightsSort,
  setAircraftFlightsSort: setAircraftFlightsSortValue,
  getAircraftFlightsFilters,
  setAircraftFlightsFilters: setAircraftFlightsFiltersValue,
}) {
  function aircraftFlights(aircraft = selectedAircraftWorkspace()) {
    return aircraftFlightsFromMap(visibleFlightEntriesByAircraft(), aircraft);
  }

  function aircraftFlightsFromMap(entriesByAircraft, aircraft) {
    return [...(entriesByAircraft.get(aircraft) ?? [])];
  }

  function compareNullableValues(left, right, direction, comparator) {
    const leftMissing = left === null || left === undefined || left === "";
    const rightMissing = right === null || right === undefined || right === "";
    if (leftMissing && rightMissing) return 0;
    if (leftMissing) return 1;
    if (rightMissing) return -1;
    const result = comparator(left, right);
    return direction === "asc" ? result : -result;
  }

  function compareAircraftFlightFallback(left, right) {
    return compareNullableValues(trendDateValue(left, []) || null, trendDateValue(right, []) || null, "desc", (a, b) => a - b)
      // trendDateValue is day-granular (it leans on flight_date so the day stays
      // timezone-stable), so break same-day ties by the stored start time to keep
      // multiple flights from one day in chronological (latest-first) order.
      || compareNullableValues(left?.flight_start_local ?? null, right?.flight_start_local ?? null, "desc", (a, b) => String(a).localeCompare(String(b)))
      || compareNullableValues(left?.flight_start_at ?? null, right?.flight_start_at ?? null, "desc", (a, b) => String(a).localeCompare(String(b)))
      || flightDisplayTitle(left).localeCompare(flightDisplayTitle(right), undefined, { sensitivity: "base", numeric: true })
      || String(left?.id ?? "").localeCompare(String(right?.id ?? ""));
  }

  function sortedAircraftFlightsForPanel(flights = []) {
    const { key, direction } = getAircraftFlightsSort();
    const trendRows = key === "duration" ? aircraftTrendRowsForActivity(selectedAircraftWorkspace()) ?? [] : [];
    return [...flights].sort((left, right) => {
      let result = 0;
      if (key === "duration") {
        result = compareNullableValues(
          aircraftFlightDurationSecondsFromTrendRows(left, trendRows),
          aircraftFlightDurationSecondsFromTrendRows(right, trendRows),
          direction,
          (a, b) => a - b,
        );
      } else if (key === "origin" || key === "destination") {
        const field = key === "origin" ? "origin_ident" : "destination_ident";
        // Sort by the bare ident (a "near KFFZ" arrival sorts as KFFZ); flights
        // with no resolved airport sort last regardless of direction.
        result = compareNullableValues(
          left?.[field] ?? null,
          right?.[field] ?? null,
          direction,
          (a, b) => a.localeCompare(b, undefined, { sensitivity: "base", numeric: true }),
        );
      } else {
        result = compareNullableValues(trendDateValue(left, []) || null, trendDateValue(right, []) || null, direction, (a, b) => a - b);
      }
      return result || compareAircraftFlightFallback(left, right);
    });
  }

  function setAircraftFlightsSort(key) {
    if (!["date", "duration", "origin", "destination"].includes(key)) return;
    const current = getAircraftFlightsSort();
    const next = current.key === key
      ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
      : { key, direction: ["origin", "destination"].includes(key) ? "asc" : "desc" };
    setAircraftFlightsSortValue(next);
    renderAircraftFlightsPanel(selectedAircraftWorkspace());
  }

  function updateAircraftFlightSortHeaders() {
    const current = getAircraftFlightsSort();
    const directionLabel = current.direction === "asc" ? "ascending" : "descending";
    const buttons = document.querySelectorAll("[data-aircraft-flight-sort]");
    buttons.forEach((button) => {
      const active = button.dataset.aircraftFlightSort === current.key;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
    // aria-sort lives on the th, but the route column holds two sort buttons
    // (From/To). Set it once per th from whichever of its buttons is active, so a
    // later inactive button in the same cell can't reset an active one to "none".
    const seen = new Set();
    buttons.forEach((button) => {
      const th = button.closest("th");
      if (!th || seen.has(th)) return;
      seen.add(th);
      const active = [...th.querySelectorAll("[data-aircraft-flight-sort]")]
        .some((candidate) => candidate.dataset.aircraftFlightSort === current.key);
      th.setAttribute("aria-sort", active ? directionLabel : "none");
    });
  }

  function aircraftFlightSearchText(entry, durationLabel, summary) {
    return [
      flightDateLabel(null, entry),
      flightDisplayTitle(entry),
      ...aircraftFlightSourceFilenames(entry),
      aircraftFlightMonitorReference(entry),
      flightRouteSummaryText(entry),
      durationLabel,
      summary?.label,
      ...(summary?.titles ?? []),
      flightIncludedInTrendAnalysis(entry) ? "included" : "excluded",
      flightReportAvailable(entry) ? "open pdf generated report" : "generate pdf missing report",
    ].filter(Boolean).join(" ").toLowerCase();
  }

  function aircraftFlightFlagClass(summary) {
    if (summary?.state !== "ready") return summary?.state ?? "not-analyzed";
    if (summary?.highCht || Number(summary?.reviewCount) > 0) return "review";
    if (Number(summary?.watchCount) > 0) return "watch";
    return "clear";
  }

  function aircraftFlightDateRangeStart(flights = [], range = "all") {
    if (range === "all") return null;
    const latest = Math.max(...flights.map((entry) => trendDateValue(entry, []) || 0));
    if (!Number.isFinite(latest) || latest <= 0) return null;
    if (range === "latest-day") {
      const latestDate = new Date(latest);
      return new Date(latestDate.getFullYear(), latestDate.getMonth(), latestDate.getDate()).getTime();
    }
    const days = range === "last-year" ? 365 : 90;
    return latest - days * 24 * 60 * 60 * 1000;
  }

  function aircraftFlightMatchesFilters(entry, context) {
    const {
      query,
      flags,
      trend,
      dateStart,
      durationLabel,
      summary,
    } = context;
    if (dateStart) {
      const value = trendDateValue(entry, []) || 0;
      if (!value || value < dateStart) return false;
    }
    if (query && !aircraftFlightSearchText(entry, durationLabel, summary).includes(query)) return false;
    const flagClass = aircraftFlightFlagClass(summary);
    if (flags !== "all" && flagClass !== flags) return false;
    if (trend !== "all") {
      const included = flightIncludedInTrendAnalysis(entry);
      if (trend === "included" && !included) return false;
      // "Excluded" is the complement of "Included": everything not feeding
      // trends — manual excludes, auto-skipped (no engine-run evidence), and
      // too-short logs. Previously auto-skipped flights were dropped from both
      // views, leaving them reachable only under "All".
      if (trend === "excluded" && included) return false;
    }
    return true;
  }

  function aircraftFlightsActiveFilterCount() {
    const filters = getAircraftFlightsFilters();
    return [
      filters.query.trim(),
      filters.dateRange !== "all",
      filters.flags !== "all",
      filters.trend !== "all",
    ].filter(Boolean).length;
  }

  function updateAircraftFlightsFilterControls() {
    const filters = getAircraftFlightsFilters();
    const search = document.querySelector("#aircraftFlightsSearch");
    if (search && document.activeElement !== search) search.value = filters.query;
    const dateFilter = document.querySelector("#aircraftFlightsDateFilter");
    if (dateFilter) {
      const allDatesOption = dateFilter.querySelector('option[value="all"]');
      if (allDatesOption) {
        const flights = aircraftFlights(selectedAircraftWorkspace());
        allDatesOption.textContent = aircraftFlightDateRangeLabel(flights) || "All dates";
      }
      dateFilter.value = filters.dateRange;
    }
    document.querySelectorAll("[data-aircraft-flight-filter]").forEach((button) => {
      const key = button.dataset.aircraftFlightFilter;
      const active = filters[key] === button.dataset.filterValue;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  function setAircraftFlightsFilter(updates = {}) {
    setAircraftFlightsFiltersValue({
      ...getAircraftFlightsFilters(),
      ...updates,
    });
    renderAircraftFlightsPanel(selectedAircraftWorkspace());
  }

  function renderAircraftFlightsPanelSummary() {
    // Kept as a no-op because the panel still calls this after filtering.
  }

  return {
    aircraftFlights,
    aircraftFlightsFromMap,
    compareNullableValues,
    compareAircraftFlightFallback,
    sortedAircraftFlightsForPanel,
    setAircraftFlightsSort,
    updateAircraftFlightSortHeaders,
    aircraftFlightSearchText,
    aircraftFlightFlagClass,
    aircraftFlightDateRangeStart,
    aircraftFlightMatchesFilters,
    aircraftFlightsActiveFilterCount,
    updateAircraftFlightsFilterControls,
    setAircraftFlightsFilter,
    renderAircraftFlightsPanelSummary,
  };
}
